/**
 * Community push — `/ws/community` (`services/qv-data/app/community/push.py`).
 *
 * The socket says **that** something changed — "group X", "your bell" — never
 * what. The panel then re-reads through the ordinary routes, which apply every
 * visibility rule; nothing here decides what anyone may see.
 *
 * The 60 s refresh stays underneath as the fallback: a socket that cannot open
 * (a proxy, a laptop asleep) costs latency, never correctness.
 */

import { useEffect, useRef } from "react";

import { idToken } from "../lib/session";
import { currentPass } from "../lib/pass";
import { actingAs } from "../lib/act-as";
import { currentUserId } from "../lib/user";
import { httpBase, wsUrl } from "./backend";

const HTTP_BASE = httpBase(
  import.meta.env.VITE_QV_COMMUNITY_URL as string | undefined,
  import.meta.env.VITE_QV_SET_URL as string | undefined,
);
/** The community API's own host, as a socket: `https:` → `wss:`. Empty disables. */
const PUSH_URL = wsUrl(
  "/ws/community",
  HTTP_BASE ? HTTP_BASE.replace(/^http/, "ws") + "/ws/community" : undefined,
);

export function communityPushConfigured(): boolean {
  return PUSH_URL !== "";
}

/** Reconnect backoff: quick at first, then once a minute at most. */
export function backoffMs(attempt: number): number {
  return Math.min(60_000, 1000 * 2 ** Math.min(attempt, 6));
}

export interface PushHandlers {
  /** These groups changed — re-read whichever is on screen. */
  onChanged: (orgs: string[]) => void;
  /** Your bell changed. */
  onBell: () => void;
}

/**
 * Keep a socket open while the panel is, watching `orgs`.
 *
 * Handlers are read through a ref so a parent re-rendering with new callbacks
 * does not tear the socket down — `screen-stream.ts`'s rule. A change to the set
 * of groups is sent as a new `watch` on the same socket.
 */
export function useCommunityPush(orgs: readonly string[], handlers: PushHandlers): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const orgsRef = useRef(orgs);
  orgsRef.current = orgs;
  const socketRef = useRef<WebSocket | null>(null);
  const watchKey = orgs.join(",");

  useEffect(() => {
    if (!PUSH_URL || typeof WebSocket === "undefined") return;
    let closed = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const watch = (ws: WebSocket) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "watch", orgs: orgsRef.current, userId: currentUserId() }));
    };

    const open = () => {
      if (closed) return;
      const ws = new WebSocket(PUSH_URL);
      socketRef.current = ws;
      ws.onopen = async () => {
        try {
          const token = await idToken();
          if (socketRef.current !== ws) return;
          if (token) {
            const actAs = actingAs() || undefined;
            const pass = currentPass() || undefined;
            ws.send(JSON.stringify({ type: "auth", token, ...(actAs ? { actAs } : {}), ...(pass ? { pass } : {}) }));
          }
        } catch {
          // No token is the same as a refused one: the server closes, we retry.
        }
        if (socketRef.current !== ws) return;
        attempt = 0;
        watch(ws);
      };
      ws.onmessage = (event) => {
        let frame: { type?: string; orgs?: string[] } | null = null;
        try {
          frame = JSON.parse(String(event.data));
        } catch {
          return;
        }
        if (frame?.type === "changed" && Array.isArray(frame.orgs)) handlersRef.current.onChanged(frame.orgs);
        else if (frame?.type === "bell") handlersRef.current.onBell();
      };
      ws.onclose = () => {
        if (socketRef.current === ws) socketRef.current = null;
        if (closed) return;
        timer = setTimeout(open, backoffMs(attempt++));
      };
    };

    open();
    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, []);

  // A different set of groups is a new `watch` on the socket already open.
  useEffect(() => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "watch", orgs: orgsRef.current, userId: currentUserId() }));
    }
  }, [watchKey]);
}
